import classNames from 'classnames';
import { useState } from 'react';

import { EXPORT_REQUEST_SENT_MESSAGE } from '@/common/components/shared/export-import/export-history/const';
import { useOpenExportHistoryModal } from '@/common/components/shared/export-import/export-history/useOpenExportHistoryModal';
import { toast } from '@/common/components/shared/toaster/toast';
import { type EmployeeExportFormat, useStartExportEmployeesMutation } from '@/common/graphql';
import { useGoogleOfflineToken } from '@/common/hooks/general/useGoogleOfflineToken';
import { ApiUtils } from '@/common/utils';

interface Props {
  /**
   * The export format the generated template targets. Determines which blank import
   * spreadsheet the backend produces (e.g. Create Employee vs. Managerial Accounting).
   */
  format: EmployeeExportFormat;
  /** Called when the export request is dispatched, before the async template build begins. */
  onStarted: () => void;
  /**
   * Called once the export request is accepted (or fails). The template is built asynchronously
   * (emailed on completion / visible in Export History), so no result URL is available here —
   * unlike a synchronous export, this callback receives no arguments.
   */
  onCompleted: () => void;
}

export const ImportTemplateAction = ({ format, onStarted, onCompleted }: Props) => {
  const [loading, setLoading] = useState(false);
  const { verifyToken } = useGoogleOfflineToken();
  const openExportHistoryModal = useOpenExportHistoryModal();

  const [startExportEmployees] = useStartExportEmployeesMutation({
    onError(error) {
      ApiUtils.handleApiError(error);
    },
  });

  const generateTemplate = async () => {
    try {
      onStarted();
      setLoading(true);

      await verifyToken(import.meta.env.VITE_EMPLOYEE_CALLBACK_URL);

      await startExportEmployees({
        variables: {
          format,
          timezoneOffset: new Date().getTimezoneOffset(),
          filter: null,
          employeeIds: null,
          sort: null,
        },
      });
      toast.success(EXPORT_REQUEST_SENT_MESSAGE);
      openExportHistoryModal('employee', 'Export Template');
      onCompleted();
    } catch (_e) {
      onCompleted();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={classNames('absolute right-0 top-0.5 text-2xs', {
        'pointer-events-none text-gray-7': loading,
        'cursor-pointer text-link': !loading,
      })}
      onClick={generateTemplate}
    >
      Open Import Spreadsheet Template
    </div>
  );
};
